import React from "react";
import { useCategoryForm } from "../../hooks/useCategoryForm";
import { ModalWrapper } from "../../../../../components/ModalWrapper";
import CategoryNameField from "./CategoryNameField";
import CategoryTypeSelect from "./CategoryTypeSelect";
import CommonButtons from "../../../../../components/CommonButtons";

interface CategoryModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (categoryName: string, type: string, displayOrder?: string) => void;
  initialName?: string;
  initialType?: string;
  initialDisplayOrder?: number;
  isEdit?: boolean;
  businessType?: string;
  error?: boolean;
}

const CategoryModal: React.FC<CategoryModalProps> = ({ open, onClose, onSubmit, initialName = "", initialType = "", initialDisplayOrder, isEdit = false, businessType = "", error = false }) => {
  const { categoryName, type, displayOrder, hasError, setHasError, handleCategoryNameChange, handleTypeChange, handleDisplayOrderChange, resetForm } = useCategoryForm(initialName, initialType, error, open, initialDisplayOrder);

  // Only enable Confirm/Update if something changed in edit mode
  const isNameValid = categoryName.trim().length > 0;
  const initialOrderStr = initialDisplayOrder !== undefined && initialDisplayOrder !== null ? String(initialDisplayOrder) : "";
  const isNameChanged = categoryName.trim() !== initialName;
  const isTypeChanged = type !== initialType;
  const isOrderChanged = displayOrder.trim() !== "" && displayOrder !== initialOrderStr;

  const visible = isEdit ? isNameValid && (isNameChanged || isTypeChanged || isOrderChanged) : isNameValid;

  const handleSubmit = () => {
    if (categoryName.trim()) {
      onSubmit(categoryName, type, displayOrder);
    } else {
      setHasError(true);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && visible) handleSubmit();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!open) return null;

  return (
    <ModalWrapper title={isEdit ? "Edit Category" : "Add Category"} onClose={handleClose} width="max-w-md">
      <div className="space-y-3">
        <CategoryNameField
          value={categoryName}
          error={hasError}
          onChange={handleCategoryNameChange}
          onKeyPress={handleKeyPress}
          displayOrder={displayOrder}
          onDisplayOrderChange={handleDisplayOrderChange}
          isEdit={isEdit}
        />

        {businessType === "Saloon" && <CategoryTypeSelect value={type} onChange={handleTypeChange} />}
      </div>
      <div className="flex justify-end gap-3 mt-6">
        <CommonButtons className="w-36 h-10" label="Cancel" variant="outlineDanger" onClick={handleClose} />
        <CommonButtons className="w-36 h-10" label={isEdit ? "Update" : "Confirm"} onClick={visible ? handleSubmit : undefined} disabled={!visible} />
      </div>
    </ModalWrapper>
  );
};

export default CategoryModal;
